import type { InternalRequest } from '../client.js';

// Types verified against a REAL server response, not hand-derived from the
// spec — the whole point of Task 16's Step 1 (see docs/superpowers/sdd/
// 2026-08-11-respond/task-16-report.md for the captured JSON). Mirrors
// server/src/incidents/incident-state.ts's exported unions verbatim.
export type IncidentSeverity = 'low' | 'medium' | 'high' | 'critical';
// No 'acknowledged' value — acknowledgement is DERIVED (spec §4.1, decision
// 3) and arrives as its own `acknowledged`/`acknowledgedAt` pair alongside
// `status`. Never invent a fourth status client-side.
export type IncidentStatus = 'open' | 'resolved' | 'closed';
export type IncidentSourceType = 'sos' | 'alert' | 'manual';

/** Mirrors server/src/incidents/incident.service.ts's IncidentSummary —
 *  written once at close and never recomputed, so these numbers can't drift
 *  from the after-action record. `closedAt` is non-null (only ever produced
 *  BY the close), everything else mirrors the incident's own nullability. */
export interface IncidentSummary {
  severity: IncidentSeverity;
  source: IncidentSourceType;
  raisedAt: string;
  acknowledgedAt: string | null;
  resolvedAt: string | null;
  closedAt: string;
  timeToAcknowledgeSeconds: number | null;
  timeToResolveSeconds: number | null;
  escalationLevelsFired: number;
  peopleNotified: number;
  participants: number;
  messages: number;
}

// ── GET / (list) ─────────────────────────────────────────────────────────

/** One row of GET /api/incidents — admin/manager only, cursor-paginated,
 *  open-first (server's own ORDER BY status_rank DESC, raised_at DESC). Every
 *  field verified present on a real response for all three source types. */
export interface IncidentListItem {
  id: string;
  title: string;
  severity: IncidentSeverity;
  status: IncidentStatus;
  sourceType: IncidentSourceType;
  deviceId: number | null;
  subjectUserId: string | null;
  subjectUserName: string | null;
  raisedBy: string | null;
  raisedByName: string | null;
  raisedAt: string;
  assignedTo: string | null;
  assignedToName: string | null;
  escalationLevel: number;
  /** Derived — reads the SOURCE row's ack (sos_alerts / rule_events) for a
   *  sourced incident, or the incident's own column for a manual one. Never
   *  a stored 'acknowledged' status. */
  acknowledged: boolean;
  acknowledgedAt: string | null;
}

export interface IncidentListResponse {
  items: IncidentListItem[];
  nextCursor: string | null;
}

// ── GET /:id (detail) + PATCH /:id ──────────────────────────────────────

/** One row of the append-only incident_events trail. `type` is left as a
 *  plain string (not narrowed to the DB CHECK's fixed vocabulary) — same
 *  choice musters.ts's own MusterEventItem makes for the identical reason:
 *  nothing here guarantees only the known values ever reach this field, and
 *  `metadata` is genuinely polymorphic per event type. */
export interface IncidentEvent {
  id: string;
  type: string;
  actorId: string | null;
  actorName: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

/** GET /api/incidents/:id response — admin/manager only (spec §9.2 point 1:
 *  the detail shape never projects a coordinate at all, so the admin/manager
 *  floor IS the whole staff-location-privacy guard here). `trail` is the
 *  first page of events, chronological (oldest first) — a dedicated,
 *  cursor-paginated GET /:id/events exists for walking a long history. */
export interface IncidentDetail {
  id: string;
  title: string;
  description: string | null;
  severity: IncidentSeverity;
  status: IncidentStatus;
  sourceType: IncidentSourceType;
  deviceId: number | null;
  subjectUserId: string | null;
  subjectUserName: string | null;
  raisedBy: string | null;
  raisedByName: string | null;
  raisedAt: string;
  assignedTo: string | null;
  assignedToName: string | null;
  escalationLevel: number;
  escalationNextAt: string | null;
  resolvedBy: string | null;
  resolvedByName: string | null;
  resolvedAt: string | null;
  closedBy: string | null;
  closedByName: string | null;
  closedAt: string | null;
  /** Frozen once at close (IncidentSummary); null until then. */
  summary: IncidentSummary | null;
  /** The incident room's conversation id — spec §9.1 "room id". */
  conversationId: string | null;
  acknowledged: boolean;
  acknowledgedAt: string | null;
  trail: IncidentEvent[];
  /** Cursor to pass as `GET /:id/events?before=` for the page of events
   *  OLDER than `trail`'s oldest entry, or `null` when `trail` already holds
   *  the complete history back to `raised`. `trail` is capped at 50 events
   *  server-side — this is the ONLY reliable way to tell "exactly 50 events,
   *  none earlier" apart from "50 events, more exist"; `trail.length` alone
   *  cannot distinguish them. A truncated, undetectable trail silently drops
   *  the START of an incident's story (the earliest events, not the most
   *  recent), which is the worst half to lose from an after-action record. */
  trailNextCursor: string | null;
}

// ── GET /:id/events ──────────────────────────────────────────────────────

export interface IncidentEventsResponse {
  events: IncidentEvent[];
  nextCursor: string | null;
}

// ── GET /:id/notifications ──────────────────────────────────────────────

/** One broadcast_recipients row read through the escalation sweep's linked
 *  broadcasts — field-for-field what the server actually returns (verified
 *  against a real response), NOT "corrected" to line up with broadcasts.ts's
 *  own BroadcastReceiptRow for the same underlying table: `user_id` keeps
 *  its snake_case wire name (the server never camelCases it here) and
 *  `provider_ref` likewise, matching musters.ts's identical divergence for
 *  the same reason — this is muster's own MusterNotificationReceipt gotcha,
 *  reapplied. Admin/manager-only: exposes phone numbers and emails. */
export interface IncidentNotificationReceipt {
  id: string;
  user_id: string | null;
  recipientName: string | null;
  channel: string;
  endpoint: string | null;
  status: string;
  reason: string | null;
  provider_ref: string | null;
  segments: number;
  created_at: string;
  completed_at: string | null;
}

export interface IncidentNotificationsResponse {
  receipts: IncidentNotificationReceipt[];
  nextCursor: string | null;
}

// ── GET /mine ────────────────────────────────────────────────────────────

/** GET /api/incidents/mine response item — the member-facing, coordinate-
 *  free surface (spec §9.2 point 2; the mobile deep-link screen reads this).
 *  No admin floor. Bare array, not paginated (capped at 200 server-side). */
export interface IncidentMineView {
  incidentId: string;
  title: string;
  severity: IncidentSeverity;
  status: IncidentStatus;
  raisedAt: string;
  acknowledged: boolean;
  myRole: 'raiser' | 'participant' | 'subject';
}

// ── POST / (manual raise) ───────────────────────────────────────────────

export interface IncidentRaiseRequest {
  title: string;
  description?: string | null;
  severity?: IncidentSeverity;
  deviceId?: number | null;
  subjectUserId?: string | null;
  escalationPolicyId?: string | null;
}

export interface IncidentRaiseResponse {
  id: string;
  created: boolean;
}

// ── PATCH /:id ────────────────────────────────────────────────────────────

export interface IncidentPatchRequest {
  title?: string;
  description?: string | null;
  severity?: IncidentSeverity;
  assignedTo?: string | null;
}

interface CursorParams {
  /** Opaque, server-issued cursor — the query param is `before`, NOT
   *  `cursor` (a wrong name is silently ignored and just re-serves page
   *  one). Neither paginated route reads a `limit` param. */
  before?: string;
}

function qsFrom(params?: CursorParams): string {
  if (!params?.before) return '';
  const usp = new URLSearchParams();
  usp.set('before', params.before);
  return `?${usp.toString()}`;
}

export function incidentsEndpoints(req: InternalRequest) {
  return {
    /** GET /api/incidents — admin/manager only. Org-scoped list, open
     *  incidents first, cursor-paginated. */
    list: (params?: CursorParams) => req.request<IncidentListResponse>(`/incidents${qsFrom(params)}`),

    /** POST /api/incidents — admin/manager only, user-keyed rate-limited
     *  (issue #342). Manual raise. */
    raise: (data: IncidentRaiseRequest) =>
      req.request<IncidentRaiseResponse>('/incidents', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** GET /api/incidents/mine — any member, no admin floor. The caller's
     *  own participation, coordinate-free by construction. */
    mine: () => req.request<IncidentMineView[]>('/incidents/mine'),

    /** GET /api/incidents/:id — admin/manager only. Detail + trail +
     *  resolved ack + room id. */
    get: (id: string) => req.request<IncidentDetail>(`/incidents/${id}`),

    /** PATCH /api/incidents/:id — admin/manager only. Severity, assignee,
     *  title/description. 409 `incident_closed` when the incident is
     *  closed. Returns the fresh detail. */
    update: (id: string, patch: IncidentPatchRequest) =>
      req.request<IncidentDetail>(`/incidents/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),

    /** POST /api/incidents/:id/acknowledge — admin/manager only.
     *  Pass-through to the source row (spec §3.4). Returns the fresh
     *  detail. */
    acknowledge: (id: string) => req.request<IncidentDetail>(`/incidents/${id}/acknowledge`, { method: 'POST' }),

    /** POST /api/incidents/:id/resolve — admin/manager only. Returns the
     *  fresh detail. */
    resolve: (id: string) => req.request<IncidentDetail>(`/incidents/${id}/resolve`, { method: 'POST' }),

    /** POST /api/incidents/:id/close — admin/manager only. Freezes +
     *  summarises; returns the frozen IncidentSummary directly (not the
     *  full detail — mirrors muster's own POST /:id/close). */
    close: (id: string) => req.request<IncidentSummary>(`/incidents/${id}/close`, { method: 'POST' }),

    /** GET /api/incidents/:id/events — admin/manager only. The append-only
     *  trail, cursor-paginated. */
    events: (id: string, params?: CursorParams) =>
      req.request<IncidentEventsResponse>(`/incidents/${id}/events${qsFrom(params)}`),

    /** GET /api/incidents/:id/notifications — admin/manager only.
     *  Escalation receipts — exposes phone numbers and emails. */
    notifications: (id: string, params?: CursorParams) =>
      req.request<IncidentNotificationsResponse>(`/incidents/${id}/notifications${qsFrom(params)}`),

    /** GET /api/incidents/:id/export — admin/manager only, CSV ONLY
     *  (`?format=` is not a real param — there is no server-side PDF in
     *  this codebase, same as Muster). Returns the URL, not a fetch: the
     *  route requires the caller's Bearer token, which a plain
     *  `<a href>`/navigation cannot carry — triggering the actual download
     *  (fetch + blob) is left to the caller, mirroring musters.exportUrl. */
    exportUrl: (id: string) => `${req.getBaseUrl()}/incidents/${id}/export`,
  };
}
